import { useEffect, useRef, useState } from "react";
import { DateRange } from "react-date-range";
import format from "date-fns/format";
import {
  addDays,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
} from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export const DateRangeComp = (props) => {
  const typeofdate = props.typeofdate;
  const rangeJson =
    typeofdate === "blank"
      ? {
          startDate: addDays(new Date(), -29),
          endDate: new Date(),
          key: "selection",
        }
      : {
          startDate: new Date(),
          endDate: addDays(new Date(), 7),
          key: "selection",
        };

  const [activeButton, setActiveButton] = useState("last30Days"); // Default to Last 30 Days
  const [range, setRange] = useState([rangeJson]);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(""); // Explicit state for input value
  const refOne = useRef(null);

  // Initialize input value on first render
  useEffect(() => {
    if (range[0].startDate && range[0].endDate) {
      setInputValue(`${format(range[0].startDate, "MM/dd/yyyy")} to ${format(range[0].endDate, "MM/dd/yyyy")}`);
    }
  }, []);

  // Handle filterParam changes (e.g., initial load or parent component changes)
  useEffect(() => {
    if (props.filterParam) {
      // Case 1: Explicitly clearing dates
      if (props.filterParam.smsStartFilter === "" && props.filterParam.smsEndFilter === "") {
        setRange([resetEmptyState]);  // Use the reset state object
        setActiveButton(null);
        setInputValue("");
      } 
      // Case 2: Initialize with valid dates
      else if (props.filterParam.smsStartFilter) {
        try {
          const startDate = new Date(props.filterParam.smsStartFilter);
          const endDate = props.filterParam.smsEndFilter 
            ? new Date(props.filterParam.smsEndFilter)
            : new Date();
          
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            setRange([{
              startDate: startDate,
              endDate: endDate,
              key: "selection",
            }]);
            
            // Set input value explicitly
            setInputValue(`${format(startDate, "MM/dd/yyyy")} to ${format(endDate, "MM/dd/yyyy")}`);
            
            // Detect which preset button matches the date range
            detectActiveButton(startDate, endDate);
          }
        } catch (err) {
          console.error("Date parsing error:", err);
        }
      }
    }
  }, [props.filterParam]);

  const resetEmptyState = {
    startDate: "",  // Use undefined instead of null
    endDate: "",
    key: "selection"
  };

  // Function to detect which preset button should be active
  const detectActiveButton = (startDate, endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if the date range matches any of the preset buttons
    const endDay = new Date(endDate);
    endDay.setHours(0, 0, 0, 0);
    
    const startDay = new Date(startDate);
    startDay.setHours(0, 0, 0, 0);
    
    // Today
    if (startDay.getTime() === today.getTime() && endDay.getTime() === today.getTime()) {
      setActiveButton("today");
      return;
    }
    
    // Yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (startDay.getTime() === yesterday.getTime() && endDay.getTime() === yesterday.getTime()) {
      setActiveButton("yesterday");
      return;
    }
    
    // Last 7 Days
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    if (startDay.getTime() === sevenDaysAgo.getTime() && endDay.getTime() === today.getTime()) {
      setActiveButton("last7Days");
      return;
    }
    
    // Last 30 Days
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
    if (startDay.getTime() === thirtyDaysAgo.getTime() && endDay.getTime() === today.getTime()) {
      setActiveButton("last30Days");
      return;
    }
    
    // If no preset matches
    setActiveButton(null);
  };

  useEffect(() => {
    document.addEventListener("keydown", hideOnEscape, true);
    document.addEventListener("click", hideOnClickOutside, true);
    
    return () => {
      document.removeEventListener("keydown", hideOnEscape, true);
      document.removeEventListener("click", hideOnClickOutside, true);
    };
  }, []);

  const hideOnEscape = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const hideOnClickOutside = (e) => {
    if (refOne.current && !refOne.current.contains(e.target)) {
      setOpen(false);
    }
  };

  const handleSubmit = () => {
    // Only submit if there are valid dates
    if (range[0].startDate && range[0].endDate) {
      props.onClick(range);
    }
  };

  // Update input value whenever range changes
  useEffect(() => {
    if (range[0].startDate && range[0].endDate) {
      setInputValue(`${format(range[0].startDate, "MM/dd/yyyy")} to ${format(range[0].endDate, "MM/dd/yyyy")}`);
    } else {
      setInputValue("");
    }
  }, [range]);

  // Shortcut button handlers
  const handleToday = () => {
    const today = new Date();
    const newRange = [{
      startDate: startOfDay(today),
      endDate: endOfDay(today),
      key: "selection",
    }];
    setRange(newRange);
    setActiveButton("today");
    setOpen(false);
  };

  const handleYesterday = () => {
    const yesterday = addDays(new Date(), -1);
    const newRange = [{
      startDate: startOfDay(yesterday),
      endDate: endOfDay(yesterday),
      key: "selection",
    }];
    setRange(newRange);
    setActiveButton("yesterday");
    setOpen(false);
  };

  const handleLastWeek = () => {
    const today = new Date();
    const lastWeek = subWeeks(today, 1);
    const newRange = [{
      startDate: startOfWeek(lastWeek),
      endDate: endOfWeek(lastWeek),
      key: "selection",
    }];
    setRange(newRange);
    setActiveButton("lastWeek");
    setOpen(false);
  };

  const handleLastMonth = () => {
    const lastMonth = subMonths(new Date(), 1);
    const newRange = [{
      startDate: startOfMonth(lastMonth),
      endDate: endOfMonth(lastMonth),
      key: "selection",
    }];
    setRange(newRange);
    setActiveButton("lastMonth");
    setOpen(false);
  };

  const handleLast7Days = () => {
    const newRange = [{
      startDate: addDays(new Date(), -6),
      endDate: new Date(),
      key: "selection",
    }];
    setRange(newRange);
    setActiveButton("last7Days");
    setOpen(false);
  };

  const handleLast30Days = () => {
    const newRange = [{
      startDate: addDays(new Date(), -29),
      endDate: new Date(),
      key: "selection",
    }];
    setRange(newRange);
    setActiveButton("last30Days");
    setOpen(false);
  };

  const handleLastYear = () => {
    const today = new Date();
    const lastYear = today.getFullYear() - 1;
    const newRange = [{
      startDate: new Date(lastYear, 0, 1), // January 1st of last year
      endDate: new Date(lastYear, 11, 31), // December 31st of last year
      key: "selection",
    }];
    setRange(newRange);
    setActiveButton("lastYear");
    setOpen(false);
  };

  const handleAllData = () => {
    const newRange = [{
      startDate: new Date("2021-01-01"),
      endDate: new Date(),
      key: "selection",
    }];
    setRange(newRange);
    setActiveButton("allData");
    setOpen(false);
  };

  return (
    <div className="calendarWrap me-1">
      <div className="row">
        <div className="col-9 pe-1">
          <input
            value={inputValue}
            readOnly
            className="inputBox input-date"
            placeholder="Select date range"
            onClick={() => setOpen((open) => !open)}
            style={{ width: "100%" }}
          />
        </div>
        <div className="col-3 ps-0">
          <button
            id="fetchData"
            type="button"
            className="btn btn-success btn-sm"
            onClick={handleSubmit}
          >
            Go
          </button>
        </div>
      </div>

      <div ref={refOne}>
        {open && (
          <div className="calendarElement my-date-range-picker d-flex bg-white mt-2">
            <div className="d-flex flex-column justify-content-between mx-sm-1 gap-1 m-2">
              <button
                className={`btn btn-sm custom-hover ${activeButton === "today" ? "active-date-btn" : ""}`}
                style={{
                  color: activeButton === "today" ? "#fff" : "#535f7e",
                  backgroundColor: activeButton === "today" ? "#1967D2" : "transparent",
                  border: "1px solid #535f7e",
                }}
                onClick={handleToday}
              >
                Today
              </button>
              <button
                className={`btn btn-sm custom-hover ${activeButton === 'yesterday' ? 'active-date-btn' : ''}`}
                style={{
                  color: activeButton === 'yesterday' ? "#fff" : "#535f7e", 
                  backgroundColor: activeButton === 'yesterday' ? "#1967D2" : "transparent",
                  border: "1px solid #535f7e"
                }} 
                onClick={handleYesterday}
              >
                Yesterday
              </button>
              <button
                className={`btn btn-sm custom-hover ${activeButton === 'last7Days' ? 'active-date-btn' : ''}`}
                style={{
                  color: activeButton === 'last7Days' ? "#fff" : "#535f7e", 
                  backgroundColor: activeButton === 'last7Days' ? "#1967D2" : "transparent",
                  border: "1px solid #535f7e"
                }} 
                onClick={handleLast7Days}
              >
                Last 7 Days
              </button>
              <button
                className={`btn btn-sm custom-hover ${activeButton === 'lastWeek' ? 'active-date-btn' : ''}`}
                style={{
                  color: activeButton === 'lastWeek' ? "#fff" : "#535f7e", 
                  backgroundColor: activeButton === 'lastWeek' ? "#1967D2" : "transparent",
                  border: "1px solid #535f7e"
                }} 
                onClick={handleLastWeek}
              >
                Last Week
              </button>
              <button
                className={`btn btn-sm custom-hover ${activeButton === 'last30Days' ? 'active-date-btn' : ''}`}
                style={{
                  color: activeButton === 'last30Days' ? "#fff" : "#535f7e", 
                  backgroundColor: activeButton === 'last30Days' ? "#1967D2" : "transparent",
                  border: "1px solid #535f7e"
                }}  
                onClick={handleLast30Days}
              >
                Last 30 Days
              </button>
              <button
                className={`btn btn-sm custom-hover ${activeButton === 'lastMonth' ? 'active-date-btn' : ''}`}
                style={{
                  color: activeButton === 'lastMonth' ? "#fff" : "#535f7e", 
                  backgroundColor: activeButton === 'lastMonth' ? "#1967D2" : "transparent",
                  border: "1px solid #535f7e"
                }} 
                onClick={handleLastMonth}
              >
                Last Month
              </button>
              <button
                className={`btn btn-sm custom-hover ${activeButton === 'lastYear' ? 'active-date-btn' : ''}`}
                style={{
                  color: activeButton === 'lastYear' ? "#fff" : "#535f7e", 
                  backgroundColor: activeButton === 'lastYear' ? "#1967D2" : "transparent",
                  border: "1px solid #535f7e"
                }} 
                onClick={handleLastYear}
              >
                Last Year
              </button>
              <button
                className={`btn btn-sm custom-hover ${activeButton === 'allData' ? 'active-date-btn' : ''}`}
                style={{
                  color: activeButton === 'allData' ? "#fff" : "#535f7e", 
                  backgroundColor: activeButton === 'allData' ? "#1967D2" : "transparent",
                  border: "1px solid #535f7e"
                }} 
                onClick={handleAllData}
              >
                All Data
              </button>
            </div>
            <DateRange
              onChange={(item) => {
                setRange([item.selection]);
                setActiveButton(null); // Clear active button when manual selection is made
              }}
              editableDateInputs={true}
              moveRangeOnFirstSelection={false}
              ranges={range}
              months={1}
              direction="horizontal"
              className=""
              maxDate={new Date()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeComp;